import { CncReport, ProrataInformation } from '@gameonly/core';
import { Types } from 'mongoose';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { authenticatedFetch } from '../../authentication/authenticatedFetch';

const getReports = async (filter: any): Promise<CncReport[]> => {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_URL}/cnc-reports?${new URLSearchParams(
      filter,
    ).toString()}`,
  );
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  return await response.json();
};

const removeReport = async (reportId: string): Promise<CncReport> => {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_URL}/cnc-reports/${reportId}`,
    {
      method: 'DELETE',
    },
  );
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  return await response.json();
};

const createReport = async ({
  name,
  type,
  studio,
  projectId,
  prorataInformation,
}: {
  name: string;
  type: string;
  studio: string;
  projectId?: Types.ObjectId;
  prorataInformation?: ProrataInformation;
}) => {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_URL}/cnc-reports`,
    {
      method: 'POST',
      body: JSON.stringify({
        name,
        type,
        studio,
        projectId,
        prorataInformation,
      }),
    },
  );
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  return await response.json();
};

const useReports = (filter?: any) => {
  const queryClient = useQueryClient();

  const reportsQuery = useQuery(
    ['cnc-reports', JSON.stringify(filter)],
    () => getReports(filter),
    {
      useErrorBoundary: true,
    },
  );

  const createReportMutation = useMutation(
    ({
      name,
      type,
      studio,
      projectId,
      prorataInformation,
    }: {
      name: string;
      type: string;
      studio: string;
      projectId?: Types.ObjectId;
      prorataInformation?: ProrataInformation;
    }) =>
      createReport({
        name,
        type,
        studio,
        projectId,
        prorataInformation,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['cnc-reports']);
      },
    },
  );

  const removeReportMutation = useMutation(
    (reportId: string) => removeReport(reportId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['cnc-reports']);
      },
    },
  );
  return {
    reports: reportsQuery.data,
    loading: reportsQuery.isLoading || removeReportMutation.isLoading,
    loadingCreate: createReportMutation.isLoading,
    create: createReportMutation.mutateAsync,
    remove: removeReportMutation.mutateAsync,
  };
};

export default useReports;
